import { stateWord } from "@lafayette/domain";
import type { StaffPaymentListFilter, StaffPaymentListRow } from "@lafayette/schemas";
import { staffPaymentListFilters } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { hintClass, linkClass } from "../../requests/components/parts";
import { formatAgo, formatInstant } from "../../requests/format";
import { Amount } from "../../ui/amount";
import { AsyncState } from "../../ui/async-state";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { EmptyState } from "../../ui/empty-state";
import { SelectField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { Panel } from "../../ui/panel";
import { usePaymentCapabilities } from "../access";
import { EvidenceLabel, PaymentStatusBadge } from "../components/parts";
import { describePaymentError } from "../errors";
import { formatMinor, shortId } from "../format";
import { isListFilter, paymentFilterLabels, sourceKindLabels } from "../labels";
import { usePaymentList } from "../queries";
import { usePaymentsRuntime } from "../runtime";

// The Payments list (CHT-759; contract section 7.3): every payment request the staff member may read, a closed filter, newest status
// change first, page by page. Every row says FAKE or REAL (or NONE before a provider order exists). The list shows ids, amounts in paise
// formatted as rupees, codes and times: no customer contact detail and no provider payload.
//
// A concierge reads the payments of the customers they are assigned to; a supervisor and an administrator read every customer
// (`payment.read_console`). The filter lives in the address (`/payments?filter=NEEDS_RECONCILIATION`), so the Home item "payments to reconcile"
// opens this list already filtered, and the browser's back button keeps the filter. An unknown filter in the address reads as "every payment".

export function PaymentsListPage() {
  useWideLayout();
  const { capabilities, isLoading } = usePaymentCapabilities();
  const { now } = usePaymentsRuntime();
  const [params, setParams] = useSearchParams();
  const asked = params.get("filter") ?? "";
  const filter: StaffPaymentListFilter | null = isListFilter(asked) ? asked : null;
  const setFilter = (next: StaffPaymentListFilter | null) =>
    setParams(next === null ? {} : { filter: next }, { replace: true });
  const list = usePaymentList(filter, capabilities.read);

  if (isLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include payments."
      />
    );
  }

  const rows = list.data?.pages.flatMap((page) => page.rows) ?? [];
  const moment = now();

  return (
    <section className="grid gap-5">
      <PageHeader
        title="Payments"
        description="Payment requests, the provider evidence behind them, and the reconcile and refund actions."
      />

      <Notice tone="info" role="note">
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <EvidenceLabel evidence="FAKE" /> means the fake gateway made it: no money moved, and it
          is not launch evidence. <EvidenceLabel evidence="REAL" /> means the real provider made it.
        </p>
      </Notice>

      <Panel id="payments-list-title" title="Payment requests">
        <div className="max-w-sm">
          <SelectField
            id="payments-filter"
            label="Show"
            value={filter ?? ""}
            onChange={(value) => setFilter(isListFilter(value) ? value : null)}
            options={staffPaymentListFilters.map((code) => ({
              value: code,
              label: paymentFilterLabels[code],
            }))}
            placeholder="Every payment request"
          />
        </div>

        {list.isPending ? (
          <AsyncState
            kind="loading"
            title="Loading the payments"
            message="Waiting for the answer."
          />
        ) : list.isError && list.data === undefined ? (
          <AsyncState
            kind="error"
            title="The payments could not be read"
            message={describePaymentError(list.error).message}
            onRetry={() => void list.refetch()}
          />
        ) : rows.length === 0 ? (
          <AsyncState
            kind="empty"
            title="No payment requests"
            message={
              filter === null
                ? "There are no payment requests you may read yet."
                : "No payment request matches this filter."
            }
          />
        ) : (
          <>
            {list.isRefetchError ? (
              <Notice tone="warning" role="status">
                <p>The last reload failed. This list may be out of date.</p>
                <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
                  Reload now
                </Button>
              </Notice>
            ) : null}
            <PaymentTable rows={rows} now={moment} />
            {list.hasNextPage ? (
              <div>
                <Button
                  variant="outline"
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {list.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              </div>
            ) : null}
            {list.isFetchNextPageError ? (
              <Notice tone="warning" role="alert">
                <p>The next page could not be read. {describePaymentError(list.error).message}</p>
              </Notice>
            ) : null}
          </>
        )}
      </Panel>
    </section>
  );
}

const column = createColumnHelper<StaffPaymentListRow>();

function flagsOf(row: StaffPaymentListRow): string[] {
  const flags: string[] = [];
  if (row.hasOpenException) flags.push("Open exception");
  if (row.hasOpenProviderCall) flags.push("Provider call without a clear answer");
  if (row.hasRequestedRefund) flags.push("Refund waiting for the provider");
  return flags;
}

function PaymentTable({
  rows,
  now,
}: Readonly<{ rows: readonly StaffPaymentListRow[]; now: Date }>) {
  const columns = useMemo(
    () => [
      column.display({
        id: "payment",
        header: "Payment",
        meta: { rowHeader: true, className: "min-w-40 align-top" },
        cell: ({ row: { original: row } }) => (
          <div className="min-w-0">
            {/* WALK-4: the name is "Payment <short id> of <amount>, <status>", not the bare id. The full id stays in the title. */}
            <Link
              className={`${linkClass} font-semibold`}
              to={`/payments/${encodeURIComponent(row.id)}`}
              title={row.id}
            >
              Payment {shortId(row.id)}{" "}
              <span className="sr-only">
                of {formatMinor(row.amountMinor, row.currency)},{" "}
                {stateWord("payment", "staffShort", row.status)}
              </span>
            </Link>
            <span className={`${hintClass} block`}>
              {sourceKindLabels[row.sourceKind] ?? row.sourceKind}
            </span>
          </div>
        ),
      }),
      column.display({
        id: "customer",
        header: "Customer",
        meta: { className: "min-w-28 align-top" },
        cell: ({ row: { original: row } }) => (
          <span className="font-serif text-[17px] leading-6">{row.customerDisplayName}</span>
        ),
      }),
      column.display({
        id: "status",
        header: "Status",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => <PaymentStatusBadge status={row.status} />,
      }),
      column.display({
        id: "amount",
        header: "Amount",
        meta: { className: "align-top tabular-nums" },
        cell: ({ row: { original: row } }) => (
          <Amount className="text-[17px] compact:text-base">
            {formatMinor(row.amountMinor, row.currency)}
          </Amount>
        ),
      }),
      column.display({
        id: "evidence",
        header: "Evidence",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => <EvidenceLabel evidence={row.evidence} />,
      }),
      column.display({
        id: "needs-a-look",
        header: "Needs a look",
        meta: { label: "Needs a look", className: "align-top" },
        cell: ({ row: { original: row } }) => {
          const flags = flagsOf(row);
          return flags.length === 0 ? (
            <span className={hintClass}>Nothing</span>
          ) : (
            <ul className="m-0 grid list-none justify-items-start gap-1 p-0">
              {flags.map((flag) => (
                <li key={flag}>
                  <Badge
                    tone="warning"
                    className="h-auto min-h-6 py-0.5 text-[12.5px] font-semibold whitespace-normal"
                  >
                    {flag}
                  </Badge>
                </li>
              ))}
            </ul>
          );
        },
      }),
      column.display({
        id: "changed",
        header: "Changed",
        meta: { className: "align-top" },
        cell: ({ row: { original: row } }) => (
          <span title={formatInstant(row.statusChangedAt.toISOString())}>
            {formatAgo(row.statusChangedAt.toISOString(), now)}
          </span>
        ),
      }),
    ],
    [now],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(row) => row.id}
      caption="Payment requests"
      className="rounded-none border-0 bg-transparent shadow-none"
      emptyState={<EmptyState title="No payment requests" />}
    />
  );
}
